/**
 * useGeoCountry — best-effort country detection via /api/geo/detect.
 *
 * - Caches result in localStorage 24h to avoid re-pinging the backend.
 * - Admin override: set localStorage.admin_view_as_country = 'KE' (or any
 *   ISO-2) to force the storefront to render a specific region. Useful for
 *   admin review of the Afrika Shop from non-KE IPs.
 * - Returns {country_code, is_kenya, is_east_africa, is_central_africa,
 *   is_afrika_shop_region, loading, isOverride}.
 *
 * Afrika Shop covers Kenya and East Africa. Every other country sees the
 * global USD Shopify catalog.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
// Bumped after consolidating the storefront contract so stale Central-Africa
// routing decisions cannot survive in a visitor's browser.
const CACHE_KEY = 'rastyle_geo_country_v4';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const OVERRIDE_KEY = 'admin_view_as_country';
let geoRequestPromise = null;

const detectCountryOnce = () => {
  if (!geoRequestPromise) {
    geoRequestPromise = axios.get(`${API}/api/geo/detect`, { timeout: 4000 })
      .then((response) => response.data)
      .finally(() => { geoRequestPromise = null; });
  }
  return geoRequestPromise;
};

export const EAST_AFRICA = ['KE', 'UG', 'TZ', 'RW', 'ET', 'SO', 'SS', 'BI', 'DJ', 'ER'];

export const useGeoCountry = () => {
  const [state, setState] = useState({
    country_code: 'KE',
    is_kenya: true,
    is_east_africa: true,
    is_central_africa: false,
    is_afrika_shop_region: true,
    loading: true,
    isOverride: false,
  });

  useEffect(() => {
    let active = true;
    const override = localStorage.getItem(OVERRIDE_KEY);
    if (override && override.length === 2) {
      const cc = override.toUpperCase();
      setState({
        country_code: cc,
        is_kenya: cc === 'KE',
        is_east_africa: EAST_AFRICA.includes(cc),
        is_central_africa: false,
        is_afrika_shop_region: EAST_AFRICA.includes(cc),
        storefront: EAST_AFRICA.includes(cc) ? 'afrika_shop' : 'shopify',
        currency: EAST_AFRICA.includes(cc) ? 'KES' : 'USD',
        shop_path: EAST_AFRICA.includes(cc) ? '/afrika-shop' : '/shop',
        loading: false,
        isOverride: true,
      });
      return;
    }

    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.cached_at < CACHE_TTL_MS) {
        setState({ ...cached.value, loading: false, isOverride: false });
        return;
      }
    } catch { /* ignore cache parse errors */ }

    detectCountryOnce()
      .then((value) => {
        if (!active) return;
        // Backfill the canonical flag if the backend hasn't been redeployed
        if (value.is_afrika_shop_region == null) {
          value.is_afrika_shop_region = EAST_AFRICA.includes(value.country_code);
        }
        localStorage.setItem(CACHE_KEY, JSON.stringify({ value, cached_at: Date.now() }));
        setState({ ...value, loading: false, isOverride: false });
      })
      .catch(() => {
        if (active) setState((s) => ({ ...s, loading: false }));
      });
    return () => { active = false; };
  }, []);

  return state;
};

export const setGeoOverride = (cc) => {
  if (cc) localStorage.setItem(OVERRIDE_KEY, cc.toUpperCase());
  else localStorage.removeItem(OVERRIDE_KEY);
  // Force a re-detect on next mount
  localStorage.removeItem(CACHE_KEY);
};
